import { Injectable } from '@angular/core';

// Returns UTC time as "YYYY-MM-DDTHH:mm:ss.fffffff" (no Z suffix).
const TIME_API_URL = 'https://timeapi.io/api/time/current/zone?timeZone=UTC';
const TIMEOUT_MS = 4000;
const RECALIBRATE_INTERVAL_MS = 10 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class ServerTimeService {
  private offsetMs = 0;
  private recalibrateHandle: ReturnType<typeof setInterval> | undefined;

  /**
   * Fetches current UTC time from timeapi.io and stores the offset between
   * server time and local device time. Subsequent calls to now() apply this
   * offset. Falls back silently to device clock on error or timeout.
   *
   * Also starts a background recalibration loop (once per app lifetime) so
   * long-running sessions — e.g. kiosk displays left open all day — stay
   * corrected for local clock drift and absorb sudden system-clock jumps
   * (e.g. late NTP sync after boot).
   */
  async calibrate(): Promise<void> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const localBefore = Date.now();
      const res = await fetch(TIME_API_URL, { signal: controller.signal });
      if (!res.ok) return;
      const data: { dateTime: string } = await res.json();
      const localAfter = Date.now();
      // timeapi.io returns e.g. "2024-01-15T10:30:45.1234567" without Z.
      // Truncate sub-seconds and append Z to parse as UTC.
      const isoUtc = data.dateTime.split('.')[0] + 'Z';
      const serverMs = new Date(isoUtc).getTime();
      // Compensate for network round-trip by using midpoint of request window.
      this.offsetMs = serverMs - (localBefore + localAfter) / 2;
    } catch {
      // Network error or timeout — offsetMs stays at its last known good value.
    } finally {
      clearTimeout(timeout);
    }
    this.ensureRecalibrationLoop();
  }

  now(): Date {
    return new Date(Date.now() + this.offsetMs);
  }

  private ensureRecalibrationLoop(): void {
    if (this.recalibrateHandle !== undefined) return;
    this.recalibrateHandle = setInterval(() => {
      void this.calibrate();
    }, RECALIBRATE_INTERVAL_MS);
  }
}
